/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  Col,
  DatePicker,
  InputNumber,
  Layout,
  Modal,
  Row,
  Tabs,
  Typography,
} from 'antd';
import { get } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import { PageSpinner } from 'components/page-spinner';
import { TableList } from 'components/table-list';
import PdButton from 'components/pd-button';

import CustomerService from 'services/customer';
import OrderService from 'services/order';
import RentService from 'services/rent';

import { DATE_FORMAT, errHandling, priceFormat } from 'utils';
import {
  priceInputNumberFormatter,
  priceInputNumberParser,
} from 'utils/inputNumber';

import './style.scss';

const { Content } = Layout;
const { TabPane } = Tabs;
const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

export function CustomerDetail(props) {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(0);
  const [customerWalletData, setCustomerWalletData] = useState({});
  const [filterDate, setFilterDate] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [nextPage, setNextPage] = useState(1);
  const [nextPageRent, setNextPageRent] = useState(1);
  const [nextPageWallet, setNextPageWallet] = useState(1);
  const [orderList, setOrderList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageRent, setPageRent] = useState(1);
  const [pageWallet, setPageWallet] = useState(1);
  const [rentOrderList, setRentOrderList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [value, setValue] = useState({});

  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');
  const pageOrigin = urlParams.get('from');

  const { walletActivities, walletAmount } = customerWalletData;
  const walletColumns = [
    {
      name: 'Date',
      selector: row => row.created_at,
      format: (row) => {
        return dayjs(row.created_at).format('DD/MM/YYYY');
      },
      width: '33%',
      center: true,
    },

    {
      name: 'Amount',
      selector: row => row.amount,
      width: '33%',
      format: (row) => {
        return 'IDR ' + priceFormat(row.amount);
      },
      center: true,
    },
    {
      name: 'Activity',
      selector: row => row.activity,
      width: '34%',
      center: true,
    },
  ];

  async function getCustomer() {
    try {
      setLoadingPage(true);
      if (id) {
        const { data } = await CustomerService.getCustomerDetail(id);
        const CustomerDetail = data.data;
        setValue(CustomerDetail);
      }
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingPage(false);
    }
  }

  async function getCustomerOrder(page = 1, limit = 10) {
    try {
      setLoadingOrder(true);
      if (id) {
        const { data } = await OrderService.getCustomerOrders(page, limit, id);
        const OrderList = data.data;
        const metadata = data.metadata;
        setNextPage(metadata.next_cursor);
        setOrderList(OrderList);
      }
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingOrder(false);
    }
  }

  async function getCustomerRentOrder(page = 1, limit = 10) {
    try {
      setLoadingOrder(true);
      if (id) {
        const { data } = await RentService.getCustomerOrders(page, limit, id);
        const OrderList = data.data;
        const metadata = data.metadata;
        setNextPageRent(metadata.next_cursor);
        setRentOrderList(OrderList);
      }
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingOrder(false);
    }
  }

  async function getCustomerWallet() {
    setLoadingOrder(true);
    try {
      const {
        data: {
          data: { detail: { amount: walletAmount } = {}, activities = [] } = {},
          metadata: { next_cursor } = {},
        },
      } = await CustomerService.getCustomerWallet({
        cursor: pageWallet,
        end_date: filterDate[1] && `${filterDate[1]} 23:59`,
        id,
        start_date: filterDate[0],
      });
      setNextPageWallet(next_cursor);
      setCustomerWalletData((prev) => ({
        ...prev,
        walletAmount,
        walletActivities: activities,
      }));
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingOrder(false);
    }
  }

  useEffect(() => {
    getCustomer();
    getCustomerOrder(page, 10);
    getCustomerRentOrder(page, 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id) {
      navigate({
        pathname: '/customers',
      });
    }
  }, [navigate, id]);

  const seeDetailBuy = (row) => (
    <div className="button-table">
      <Link to={`/orders/detail?id=${row.id}`}>
        <PdButton type="primary" style={{ height: 32, width: 100 }}>
          See Detail
        </PdButton>
      </Link>
    </div>
  );

  const [columns, setColumns] = useState([
    {
      name: 'ID',
      selector: row => row.id,
      width: '15%',
    },
    {
      name: 'Date',
      selector: row => row.ordered_at,
      format: (row) => {
        return dayjs(row.ordered_at).format('DD/MM/YYYY');
      },
      width: '20%',
      center: true,
    },

    {
      name: 'Amount',
      selector: row => row.net_price,
      width: '20%',
      format: (row) => {
        return 'IDR ' + priceFormat(row.net_price);
      },
    },
    {
      name: 'Status',
      selector: row => row.status,
      format: (row) => {
        let str = row.status.split('_');
        str = str.map((item) => {
          return item.charAt(0).toUpperCase() + item.slice(1);
        });
        return str.join(' ');
      },
      width: '20%',
      center: true,
    },
    {
      name: '',
      cell: seeDetailBuy,
      center: true,
      width: '20%',
    },
  ]);

  function changePage(val) {
    setPage(val);
    getCustomerOrder(val, 10);
  }

  function changePageRent(val) {
    setPageRent(val);
    getCustomerRentOrder(val, 10);
  }

  function handleChangeTab() {
    setColumns([
      {
        name: 'ID',
        selector: row => row.id,
        width: '15%',
      },
      {
        name: 'Date',
        selector: row => row.ordered_at,
        format: (row) => {
          return dayjs(row.ordered_at).format('DD/MM/YYYY');
        },
        width: '20%',
        center: true,
      },

      {
        name: 'Amount',
        selector: row => row.net_price,
        width: '20%',
        format: (row) => {
          return 'IDR ' + priceFormat(row.net_price);
        },
      },
      {
        name: 'Status',
        selector: row => row.status,
        format: (row) => {
          let str = row.status.split('_');
          str = str.map((item) => {
            return item.charAt(0).toUpperCase() + item.slice(1);
          });
          return str.join(' ');
        },
        width: '20%',
        center: true,
      },
      {
        name: '',
        cell: seeDetailBuy,
        center: true,
        width: '20%',
      },
    ]);
  }

  function handleChangeDateWallet(val) {
    const [start = '', end = ''] = val;
    const start_date = start && dayjs(start).format('MM/DD/YYYY');
    const end_date = end && dayjs(end).format('MM/DD/YYYY');
    setFilterDate([start_date, end_date]);
  }

  async function handleTopUp() {
    setLoadingButton(true);
    try {
      const payload = { wallet: { amount } };
      await CustomerService.topUpWallet(id, payload);
      setShowModal(false);
      setAmount(0);
      getCustomerWallet();
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingButton(false);
    }
  }

  function handleCloseModal() {
    setShowModal(false);
    setAmount(0);
  }

  useEffect(() => {
    getCustomerWallet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, filterDate, pageWallet]);

  if (loadingPage) {
    return <PageSpinner />;
  }

  return (
    <Layout className="pd-cms-customer main-container">
      {/* Modal Top Up */}
      {showModal && (
        <Modal open={showModal} onCancel={handleCloseModal} footer={false}>
          <Row type="flex" justify="center" className="pd-margin-bottom-lg">
            <Title level={4}>Top Up Wallet</Title>
          </Row>
          <Row type="flex" justify="center" align="middle">
            <Col xs={3}>
              <Text>Amount :</Text>
            </Col>
            <Col xs={{ span: 20, offset: 1 }}>
              <InputNumber
                className="w-full"
                formatter={priceInputNumberFormatter}
                onChange={setAmount}
                parser={priceInputNumberParser}
                value={amount}
              />
            </Col>
          </Row>
          <Row type="flex" justify="center" className="pd-margin-top-md">
            <Col lg={12}>
              <PdButton
                className="height-32 w-full"
                loading={loadingButton}
                onClick={handleTopUp}
                type="primary">
                Submit
              </PdButton>
            </Col>
          </Row>
        </Modal>
      )}
      <div className="content">
        <div className="pageTitle">Customer Detail</div>
        <Content>
          <Row>
            <Col span={24} className="section-container">
              <div className=" inner-section">
                <div className="summaryContent">
                  <Row>
                    <Col span={5}>
                      <div
                        className="profile-pic"
                        style={{
                          backgroundImage: `url(${get(
                            value,
                            'profile_picture',
                            ''
                          )}), url(/img/logo/person-placeholder.png)`,
                        }}></div>
                    </Col>
                    <Col span={16} offset={1}>
                      <Row>
                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong className="">
                              First Name
                            </Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {get(value, 'first_name', '')}
                            </Text>
                          </Col>
                        </Col>

                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong className="">
                              Last Name
                            </Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {get(value, 'last_name', '')}
                            </Text>
                          </Col>
                        </Col>

                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Phone Number</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {get(value, 'phone_number', '')}
                            </Text>
                          </Col>
                        </Col>

                        <Col span={24} className="mb-15">
                          <Col span={7}>
                            <Text strong>Email</Text>
                          </Col>
                          <Col span={17}>
                            <Text type="secondary">
                              {get(value, 'email', '')}
                            </Text>
                          </Col>
                        </Col>

                        <Col span={24} className="mb-15">
                          <Col span={7} style={{ marginBottom: '0' }}>
                            <Text strong>Gender</Text>
                          </Col>
                          <Col span={17} style={{ marginBottom: '0' }}>
                            <Text type="secondary">
                              {get(value, 'gender', '')}
                            </Text>
                          </Col>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>

            <Col span={24} className="section-container">
              <div className=" inner-section">
                <Title className="pageSubtitle" level={3}>
                  Address
                </Title>
                <div className="summaryContent">
                  <Row
                    className="pd-cms-customer--address-list"
                    gutter={[20, 20]}>
                    {get(value, 'addresses', []).map((address, i) => (
                      <Col span={12} key={i}>
                        <div className="address-box">
                          <div className="head">
                            {get(address, 'title', '')}
                            {' - '}
                            {get(address, 'recipient', '')}
                          </div>
                          {get(address, 'address', '')}{' '}
                          {get(address, 'second_line_address', '') !== ''
                            ? `${get(address, 'second_line_address', '')} `
                            : ''}
                          {get(address, 'city', '')}
                          {', '}
                          {get(address, 'province', '')}{' '}
                          {get(address, 'zip_code', '')}
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </div>
            </Col>

            {/* Wallet Section */}
            <Col span={24} className="section-container">
              <div className=" inner-section">
                <Row
                  className="pd-cms-customer--wallet"
                  type="flex"
                  align="middle">
                  <Col span={24}>
                    <Title className="pageSubtitle" level={3}>
                      Wallet
                    </Title>
                  </Col>
                  <Col span={6}>
                    <Card className="balance">
                      <Text>Balance</Text>
                      <Text strong>IDR {priceFormat(walletAmount)}</Text>
                    </Card>
                  </Col>
                  <Col lg={{ span: 6, offset: 1 }}>
                    <RangePicker
                      className="w-full"
                      onChange={handleChangeDateWallet}
                      format={DATE_FORMAT}
                    />
                  </Col>
                  <Col lg={{ span: 3, offset: 8 }}>
                    <PdButton
                      className="height-32"
                      onClick={setShowModal.bind(this, true)}
                      outline={true}
                      type="primary">
                      Top Up
                    </PdButton>
                  </Col>
                  <Col
                    span={24}
                    className="pd-margin-top-md pd-customer-detail-table">
                    <TableList
                      changePage={setPageWallet}
                      columns={walletColumns}
                      loadingOrder={loadingOrder}
                      nextPage={nextPageWallet}
                      orderList={walletActivities}
                      page={pageWallet}
                    />
                  </Col>
                </Row>
              </div>
            </Col>

            {/* Order Section */}
            {!pageOrigin && (
              <Col span={24} className="section-container">
                <div className=" inner-section">
                  <Title className="pageSubtitle" level={3}>
                    Orders
                  </Title>
                  <div className="summaryContent">
                    <Row
                      className="pd-cms-customer--order-list"
                      gutter={[20, 20]}>
                      <Tabs
                        defaultActiveKey="orders"
                        onChange={handleChangeTab}>
                        <TabPane tab="Buy" key="orders">
                          <Col span={3}>Total Orders</Col>
                          <Col span={3} className="bold">
                            {get(value, 'total_order', 0)} Orders
                          </Col>
                          <TableList
                            changePage={changePage}
                            columns={columns}
                            nextPage={nextPage}
                            orderList={orderList}
                            page={page}
                            loadingOrder={loadingOrder}
                          />
                        </TabPane>

                        <TabPane tab="Rent" key="rent_orders">
                          <Col span={3}>Total Orders</Col>
                          <Col span={3} className="bold">
                            {get(value, 'total_order', 0)} Orders
                          </Col>
                          <TableList
                            changePage={changePageRent}
                            columns={columns}
                            nextPage={nextPageRent}
                            orderList={rentOrderList}
                            page={pageRent}
                            loadingOrder={loadingOrder}
                          />
                        </TabPane>
                      </Tabs>
                    </Row>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { CustomerDetail };
